import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Row, Col } from "reactstrap";
import { DatosSesion } from "../../Components/Common/DatosSesion";
import { useDashboardEdit } from "./DraggableDashboard";
import { useLayoutCache } from "./useLayoutCache";

const DraggableKPIs = ({ widgetsConfig, renderWidget }) => {
  const [items, setItems] = useState([]);
  // Use the safe context hook
  const { isEditMode } = useDashboardEdit();
  
  // Usar hook compartido para cargar layout
  const { layout: cachedLayout, loading: layoutLoading } = useLayoutCache('DraggableKPIs');

  // Procesar layout cacheado cuando esté disponible
  useEffect(() => {
    if (!layoutLoading && widgetsConfig && widgetsConfig.length > 0) {
      if (cachedLayout) {
        const orderMap = {};
        cachedLayout.forEach(item => {
          orderMap[item.id] = item.order;
        });
        
        const orderedWidgets = [...widgetsConfig].sort((a, b) => {
          const orderA = orderMap[`kpi-${a.id}`] !== undefined ? orderMap[`kpi-${a.id}`] : a.id;
          const orderB = orderMap[`kpi-${b.id}`] !== undefined ? orderMap[`kpi-${b.id}`] : b.id;
          return orderA - orderB;
        });
        
        //console.log("DraggableKPIs - Layout procesado desde caché");
        setItems(orderedWidgets);
      } else {
        // Usar orden por defecto
        //console.log("DraggableKPIs - Usando orden por defecto");
        setItems(widgetsConfig);
      }
    }
  }, [cachedLayout, layoutLoading, widgetsConfig]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    setItems(newItems);
    
    // Guardar automáticamente en localStorage
    const farmacia = DatosSesion("farmacia");
    const correo = DatosSesion("correo");
    const id = DatosSesion("id");
    const identificador = farmacia || id || correo || "default";
    const localStorageKey = `dashboard_kpi_layout_${identificador}`;
    
    const layout = newItems.map((item, index) => ({
      id: `kpi-${item.id}`,
      order: index
    }));
    
    localStorage.setItem(localStorageKey, JSON.stringify(layout));
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {isEditMode ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="kpis">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={snapshot.isDraggingOver ? "bg-light p-2 rounded" : ""}
              >
                <Row>
                  {items.map((item, index) => {
                    // Determinar el tamaño de columna según el widget
                    let colSize = { xl: 4, md: 6, sm: 12 };
                    if (item.id <= 4) {
                      colSize = { xl: 6, md: 6, sm: 12 };
                    }

                    return (
                      <Draggable key={`kpi-${item.id}`} draggableId={`kpi-${item.id}`} index={index}>
                        {(provided, snapshot) => (
                          <Col
                            xl={colSize.xl}
                            md={colSize.md}
                            sm={colSize.sm}
                          >
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="position-relative mb-3"
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.8 : 1,
                              }}
                            >
                              {/* Handle de arrastre */}
                              <div
                                {...provided.dragHandleProps}
                                className="position-absolute top-0 start-50 translate-middle-x"
                                style={{
                                  zIndex: 1001,
                                  cursor: 'grab',
                                  marginTop: '-10px',
                                  backgroundColor: '#405189',
                                  borderRadius: '50%',
                                  padding: '6px 10px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                }}
                              >
                                <i className="ri-drag-move-2-fill text-white fs-16"></i>
                              </div>
                              {renderWidget(item)}
                            </div>
                          </Col>
                        )}
                      </Draggable>
                    );
                  })}
                </Row>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Row>
          {items.map((item, index) => {
            // Determinar el tamaño de columna según el widget
            let colSize = { xl: 4, md: 6, sm: 12 };
            if (item.id <= 4) {
              colSize = { xl: 6, md: 6, sm: 12 };
            }

            return (
              <Col xl={colSize.xl} md={colSize.md} sm={colSize.sm} key={`kpi-${item.id}`}>
                {renderWidget(item, index)}
              </Col>
            );
          })}
        </Row>
      )}
    </>
  );
};

export default DraggableKPIs;
